import type { ZodType, ZodTypeDef } from 'zod';
import { ApiError, AuthError, ApiResponseValidationError, type ApiErrorBody } from './errors';

function buildHttpError(status: number, message: string, body?: ApiErrorBody): ApiError {
  return status === 401 ? new AuthError(message, body) : new ApiError(status, message, body);
}

export type GetToken = () => string | null | undefined | Promise<string | null | undefined>;

export interface HttpClientConfig {
  baseUrl: string;
  getToken?: GetToken;
  /** Custom fetch (e.g. for tests or React Native polyfills). Defaults to globalThis.fetch. */
  fetch?: typeof globalThis.fetch;
  /**
   * Headers attached to every request. Authorization from `getToken` always
   * wins so the dev build can set `X-Dev-User` without breaking real auth
   * once Keycloak is wired up.
   */
  defaultHeaders?: Record<string, string>;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  /** Multipart form data — mutually exclusive with `body`. Content-Type is omitted so fetch sets the boundary automatically. */
  formData?: FormData;
  /**
   * If provided, the response body is parsed and validated against this Zod schema.
   * `Input` is left open (any) so schemas that use `.default(...)` — which makes the
   * input shape diverge from the output — still satisfy the constraint. The parsed
   * value (Output) is what we return.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod input type is intentionally open; see comment above
  responseSchema?: ZodType<T, ZodTypeDef, any>;
  /** If true, do not attempt to parse a response body (for 204s). */
  expectEmpty?: boolean;
  signal?: AbortSignal;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly getToken: GetToken | undefined;
  private readonly fetchImpl: typeof globalThis.fetch;
  private readonly defaultHeaders: Record<string, string>;

  constructor(config: HttpClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.getToken = config.getToken;
    this.fetchImpl = config.fetch ?? globalThis.fetch.bind(globalThis);
    this.defaultHeaders = config.defaultHeaders ?? {};
  }

  async requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { ...this.defaultHeaders, Accept: '*/*' };
    if (this.getToken) {
      const token = await this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const response = await this.fetchImpl(url, { method: 'GET', headers, signal });
    if (!response.ok) {
      const body = await safeJson<ApiErrorBody>(response);
      throw buildHttpError(
        response.status,
        body?.message ?? `HTTP ${response.status} ${response.statusText}`,
        body,
      );
    }
    return response.blob();
  }

  async request<T>(opts: RequestOptions<T>): Promise<T> {
    const url = `${this.baseUrl}${opts.path}`;
    const headers: Record<string, string> = { ...this.defaultHeaders, Accept: 'application/json' };
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.getToken) {
      const token = await this.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const body = opts.formData ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined);
    const response = await this.fetchImpl(url, {
      method: opts.method ?? 'GET',
      headers,
      body,
      signal: opts.signal,
    });

    if (!response.ok) {
      const body = await safeJson<ApiErrorBody>(response);
      throw buildHttpError(
        response.status,
        body?.message ?? `HTTP ${response.status} ${response.statusText}`,
        body,
      );
    }

    if (opts.expectEmpty || response.status === 204) {
      return undefined as T;
    }

    const raw = (await response.json()) as unknown;
    if (!opts.responseSchema) return raw as T;

    const parsed = opts.responseSchema.safeParse(raw);
    if (!parsed.success) {
      throw new ApiResponseValidationError(
        `Response from ${opts.method ?? 'GET'} ${opts.path} did not match expected schema`,
        parsed.error.issues,
      );
    }
    return parsed.data;
  }
}

async function safeJson<T>(response: Response): Promise<T | undefined> {
  try {
    return (await response.json()) as T;
  } catch {
    return undefined;
  }
}
